<template>
  <div class="child-wrapper">
    <section id="category-overall">
      <article>
        <div class="row clearfix">
          <h1>
            Overall Category Highlights
          </h1>
          <div class="sixcol">
            <p>In 2025, <strong>Circus and Physical Theatre</strong>, <strong>Classical Music</strong>, <strong>Festivals (Contemporary Music)</strong>, <strong>Festivals (Multi-Category)</strong> and <strong>Opera</strong> recorded y-o-y growth in both revenue and attendance. <strong>Contemporary Music</strong>, <strong>Musical Theatre</strong>, <strong>Festivals (Contemporary Music)</strong>, <strong>Classical Music</strong> and <strong>Comedy</strong> were significant contributors to the live performance industry. The industry’s overall average ticket price increased and was largely attributable to increased operating costs across the live performance industry.</p>
            <ul>
              <li><strong>Circus and Physical Theatre</strong>* recorded the highest growth in revenue (by 159.6% to $64.7m) and attendance (by 88.1% to 0.7m) among all categories in 2025. The increase in the average ticket price (by 40.3% to $95.32) further contributed to the growth in revenue. Growth in this category fluctuates annually due to international touring. The significant growth in 2025 was primarily driven by the inclusion of data from Cirque du Soleil’s national tour of <em>Corteo</em>. Other top events included <em>Now You See Me Live</em>, <em>Blanc de Blanc Encore</em>, <em>Penn &amp; Teller</em> and <em>Cirque Alice</em>. Despite this growth, <strong>Circus and Physical Theatre</strong> accounted for a relatively small share of total industry revenue (1.9%) and attendance (2.4%).</li>
              <li><strong>Contemporary Music</strong> recorded a marginal y-o-y decline in revenue (by 0.9% to $1.8b) and attendance (by 2.4% to 13.7m) in 2025. The increase in the average ticket price (by 2.0% to $138.15) partially offset the decline in revenue. These results are the second-highest recorded for this category. <strong>Contemporary Music</strong> accounted for the largest market share of revenue (53.5%) and attendance (44.8%). Between 2011 and 2025, <strong>Contemporary Music</strong> revenue increased by 232.7% and attendance increased by 131.4%. Major tours in 2025 included Lady Gaga, Oasis, AC/DC, Luke Combs, Drake, Tyler, The Creator, Billie Eilish, Metallica and Kendrick Lamar.</li>
            </ul>
          </div>
          <div class="sixcol last">
            <ul>
              <li><strong>Musical Theatre</strong> recorded a y-o-y decline in revenue (by 16.8% to $442.3m) and attendance (by 16.6% to 3.7m) in 2025. The average ticket price decreased marginally (by 0.2% to $127.15). <strong>Musical Theatre</strong> accounted for the second-largest market share of revenue (13.2%) and attendance (11.9%) among all categories in 2025. However, its share of the overall live performance market declined compared with 2024, when the category accounted for 15.9% of revenue and 13.9% of attendance. Between 2011 and 2025, <strong>Musical Theatre</strong> revenue increased by 79.2% and attendance increased by 30.4%. Top events in 2025 were <em>MJ the Musical</em>, <em>Beetlejuice The Musical</em>, <em>The Book of Mormon</em>, <em>Jesus Christ Superstar</em> and <em>Beauty and the Beast</em>.</li>
              <li><strong>Festivals (Contemporary Music)</strong> recorded y-o-y growth in revenue (by 20.7% to $399.9m) and attendance (by 4.9% to 2.1m) in 2025. These are the highest-ever recorded revenue and attendance results for this category. The increase in the average ticket price (by 14.1% to $206.17) further contributed to the growth in revenue. The return of festivals that did not proceed in 2024, such as Spilt Milk, also contributed to the category’s growth. Between 2011 and 2025, revenue increased by 314.6% and attendance increased by 114.1%, partly reflecting the incremental inclusion of new data providers over time. Major festivals included Beyond The Valley, Bluesfest, Knockout Outdoor 2025: Return of the Circuz, Spilt Milk, Laneway Festival, Hypersonic Festival, CMC Rocks and Good Things Festival.</li>
              <li><strong>Classical Music</strong> recorded the third-highest y-o-y growth in revenue (by 28.6% to $133.1m) and the fourth-highest y-o-y growth in attendance (by 6.7% to 1.5m) among all categories in 2025. The increase in the average ticket price (by 19.2% to $106.10) further contributed to the growth in revenue. These are the highest-ever recorded revenue and attendance results for this category. Between 2011 and 2025, <strong>Classical Music</strong> revenue increased by 121.4% and attendance increased by 41.6%. Major events included <em>Hans Zimmer Live</em>, <em>The Music of Joe Hisaishi</em>, <em>The Salvation Army’s Carols in the Domain</em> and performances by Andrea Bocelli.</li>
            </ul>
          </div>

          <p style="font-size: .8em;text-align: center;">*Cirque du Soleil’s <em>Luzia</em> was presented in NSW in 2024; however, data for this event were not included in the 2024 report. In 2025, data for Cirque du Soleil’s <em>Corteo</em> have been included, which appears to be a major contributor to the significant growth observed in Circus and Physical Theatre.</p>
        </div>
      </article>

      <figure class="chart">
        <div class="row clearfix">
          <div class="row callouts clearfix" v-if="activeReport">
            <aside id="category-overall-toprev" class="sixcol">
              <h3 style="margin-top: 0.35em;">Highest Revenue</h3>
              <h2 style="visibility: visible;">
                $<ICountUp :endVal="activeReport.highestRevenue.value" :options="{decimalPlaces: 2}"/>b
              </h2>
              <h3>{{ activeReport.highestRevenue.cat }}</h3>
            </aside>

            <aside id="category-overall-topatt" class="sixcol last">
              <h3 style="margin-top: 0.35em;">Highest Attendance</h3>
              <h2 style="visibility: visible;">
                <ICountUp :endVal="activeReport.highestAttendance.value" :options="{decimalPlaces: 2}"/>m
              </h2>
              <h3>{{ activeReport.highestAttendance.cat }}</h3>
            </aside>
          </div>
          <div class="callouts clearfix">
            <aside id="category-overall-revchange" class="sixcol">
              <h3 style="margin-top: 0.35em;">Biggest Revenue Change</h3>
              <h2 style="visibility: visible;">
                {{ activeReport.biggestRevenueChange.dir==='up' ? '↑' : '↓' }}
                <ICountUp :endVal="activeReport.biggestRevenueChange.value" :options="{decimalPlaces: 1}"/>%
              </h2>
              <h3>{{ activeReport.biggestRevenueChange.cat }}</h3>
            </aside>
            <aside id="category-overall-attchange" class="sixcol last">
              <h3 style="margin-top: 0.35em;">Biggest Attendance Change</h3>
              <h2 style="visibility: visible;">
                {{ activeReport.biggestAttendanceChange.dir==='up' ? '↑' : '↓' }}
                <ICountUp :endVal="activeReport.biggestAttendanceChange.value" :options="{decimalPlaces: 1}"/>%
              </h2>
              <h3>{{ activeReport.biggestAttendanceChange.cat }}</h3>
            </aside>
          </div>
        </div>
      </figure>

      <div class="fullbg">
        <p class="caption">Photo: <em>Darwin Comedy Festival - Sammy J</em>, AANT Centre, Photo Credit: Paz Tassone</p>
      </div>
    </section>

    <CategoryTrends></CategoryTrends>

    <CategoryMovement></CategoryMovement>
  </div>
</template>

<script>
/*eslint-disable */
import CategoryTrends from './CategoryTrends'
import CategoryMovement from './CategoryMovement'

export default {
  components: {
    CategoryTrends,
    CategoryMovement,
  },
  data () {
    return {
      activeYear: 2025,
      reports: [
        {
          year: 2025,
          highestRevenue: {
            cat: 'Contemporary Music',
            value: 1.79
          },
          highestAttendance: {
            cat: 'Contemporary Music',
            value: 13.74
          },
          biggestRevenueChange: {
            dir: 'up',
            cat: 'Circus and Physical Theatre',
            value: 159.6
          },
          biggestAttendanceChange: {
            dir: 'up',
            cat: 'Circus and Physical Theatre',
            value: 88.1
          }
        }
      ],
    }
  },
  computed: {
    activeReport () {
      const self = this

      for (let i = 0; i < self.reports.length; i++) {
        const report = self.reports[i]
        if (report.year === self.activeYear) {
          return report
        }
      }

      return false
    }
  },
}
</script>

<style lang="scss" scoped>
#category-overall .fullbg {
  background-image: url("~@/assets/img/2025/15-Overall-Category-DEC_DARWIN_COMEDY_FESTIVAL_Sammy-J-121_image-Paz-Tassone.jpg");
}
</style>
